/**
 * Writing on a picture and sticking emojis to it, the way Snapchat does: the
 * one editor behind a snap and a GiviSnap alike.
 *
 * A tap on the picture starts writing. One caption and up to
 * SNAP_STICKERS_MAX emojis, each carried by a finger and pinched and twisted
 * by two -- anywhere on the screen, not only on the thing itself, since on a
 * phone a caption is often narrower than two fingers.
 *
 * It is a hook rather than a screen, because the two screens lay it out
 * differently: a snap on a stage its own shape with a blurred backdrop, a
 * GiviSnap across the whole screen with a selfie in the corner. Each takes
 * the parts and puts them where they go:
 *
 *  - `surface`, the handlers for whatever a tap or a pinch should land on;
 *  - `layer`, what is laid on the picture, inside the picture's own box;
 *  - `tools`, Aa, the emoji button and the bin;
 *  - `overlays`, the typing layer and the emoji picker.
 *
 * None of it is burned into the picture: it is kept as data beside it and
 * drawn over it wherever it is shown.
 */
import { useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { SNAP_CAPTION_MAX, SNAP_STICKERS_MAX } from '../../shared/constants';
import type { CaptionAt, SnapDecor, SnapSticker } from '../../shared/types';
import { CAPTION_HOME, lookClasses, styleFor } from './Caption';
import {
  CAPTION_COLORS,
  CAPTION_FONTS,
  CAPTION_STYLE_DEFAULT,
  captionStyleOf,
  type CaptionColor,
  type CaptionFont,
  type CaptionStyle,
} from '../../shared/captionStyle';
import { gripOf, hasMoved, placeAt, stretchAt, swipeOf, type Grip, type Point } from './snapGesture';
import { PHOTO_REACH, type Reach } from '../../shared/snapDecor';
import { artUrl } from '../../shared/stickerArt';
import { EmojiPicker, IconSmile, IconTrash } from './ui';

/**
 * Where each new emoji lands: near the middle, a little further along each
 * time, so a second one never hides exactly behind the first.
 */
export function stickerHome(count: number): CaptionAt {
  const step = (count % 5) - 2;
  return { x: 0.5 + step * 0.08, y: 0.4 + step * 0.06, rotate: 0, scale: 1 };
}

/** What goes with the picture, leaving out whatever was never written. */
export function decorFrom(
  caption: string,
  captionAt: CaptionAt,
  stickers: SnapSticker[],
  captionStyle?: CaptionStyle,
): SnapDecor {
  const text = caption.trim();
  // The default look is left out, so a plain caption goes as it always has.
  const look = captionStyleOf(captionStyle);
  return {
    ...(text ? { caption: text, captionAt, ...(look ? { captionStyle: look } : {}) } : {}),
    ...(stickers.length ? { stickers } : {}),
  };
}

/** What each face and colour is called on its button. */
const FONT_NAMES: Record<CaptionFont, string> = {
  classic: 'Classic',
  serif: 'Serif',
  marker: 'Marker',
  script: 'Script',
  typewriter: 'Typewriter',
};
const COLOR_NAMES: Record<CaptionColor, string> = {
  white: 'White',
  black: 'Black',
  yellow: 'Yellow',
  red: 'Red',
  pink: 'Pink',
  blue: 'Blue',
  green: 'Green',
  purple: 'Purple',
};

/*
 * Pressing a face or a colour must not take the keyboard away: the typing
 * layer closes when its field loses focus, and a button takes focus on press.
 */
const keepTyping = (event: { preventDefault: () => void }) => event.preventDefault();

/** What is laid on the picture: the words, or an emoji by its place in the list. */
type Target = 'caption' | number;

/** What a finger came down on, if it was anything laid on the picture. */
function targetOf(element: EventTarget): Target | null {
  const on = (element as Element).closest?.('[data-caption], [data-sticker]');
  if (!on) return null;
  return on.hasAttribute('data-caption') ? 'caption' : Number(on.getAttribute('data-sticker'));
}

export interface DecorEditorOptions {
  /**
   * How far off the picture words and emojis may go: the picture alone by
   * default, or the frame round it (reachOf) for a snap.
   */
  reach?: Reach;
  /** A sideways swipe across the picture, off everything: 1 on, -1 back. */
  onSwipe?: (step: 1 | -1) => void;
}

export function useDecorEditor(options: DecorEditorOptions = {}) {
  // Read when a finger moves, so the latest is used without the handlers changing.
  const latest = useRef(options);
  latest.current = options;
  const [caption, setCaption] = useState('');
  const [captionAt, setCaptionAt] = useState<CaptionAt>(CAPTION_HOME);
  const [captionStyle, setCaptionStyle] = useState<CaptionStyle>(CAPTION_STYLE_DEFAULT);
  const [writing, setWriting] = useState(false);
  const [stickers, setStickers] = useState<SnapSticker[]>([]);
  const [chosen, setChosen] = useState<number | null>(null);
  const [picking, setPicking] = useState(false);
  const [held, setHeld] = useState<Target | null>(null);
  const frame = useRef<HTMLSpanElement>(null);

  /** Every finger down, by pointer id; two at most. */
  const fingers = useRef(new Map<number, Point>());
  /** What the fingers are moving, and where it was when they took hold. */
  const grip = useRef<{ target: Target | null; grip: Grip; moved: boolean; first: Target | null; many: boolean } | null>(null);
  /** The last thing touched: what a pinch with neither finger on anything moves. */
  const active = useRef<Target | null>(null);
  /** A finger went down and came up without moving, on this (or on nothing). */
  const tapped = useRef<{ target: Target | null } | null>(null);
  /**
   * An edge of the words held by one finger: the box widens or narrows from
   * that side, and nothing else moves until it lets go.
   */
  const stretching = useRef<{
    id: number;
    edge: 'left' | 'right';
    start: Point;
    from: CaptionAt;
    width: number;
  } | null>(null);

  // Escape backs out one step: the picker, then the keyboard. Taken on the
  // way down, before any Escape of the screen's own, which would close it.
  useEffect(() => {
    if (!picking && !writing) return;
    const key = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      if (picking) setPicking(false);
      else setWriting(false);
    };
    window.addEventListener('keydown', key, true);
    return () => window.removeEventListener('keydown', key, true);
  }, [picking, writing]);

  // Having just written, the words are what a pinch anywhere should size.
  useEffect(() => {
    if (writing) active.current = 'caption';
  }, [writing]);

  const full = stickers.length >= SNAP_STICKERS_MAX;
  const shown = caption.trim() !== '';

  const addSticker = (emoji: string) => {
    setPicking(false);
    setStickers((now) => {
      active.current = now.length;
      return [...now, { emoji, at: stickerHome(now.length) }];
    });
    setChosen(null);
  };

  const removeChosen = () => {
    if (chosen === null) return;
    setStickers((now) => now.filter((_, i) => i !== chosen));
    setChosen(null);
    active.current = null;
  };

  const placeOf = (target: Target): CaptionAt =>
    target === 'caption' ? captionAt : (stickers[target]?.at ?? CAPTION_HOME);

  const place = (target: Target, at: CaptionAt) => {
    if (target === 'caption') setCaptionAt(at);
    else setStickers((now) => now.map((each, i) => (i === target ? { ...each, at } : each)));
  };

  /** What a pinch off everything moves: the last thing touched, else the words, else the newest emoji. */
  const fallback = (): Target | null => {
    const last = active.current;
    if (last === 'caption' && shown) return last;
    if (typeof last === 'number' && last < stickers.length) return last;
    if (shown) return 'caption';
    return stickers.length ? stickers.length - 1 : null;
  };

  const retake = (target: Target | null) => {
    const points = [...fingers.current.values()];
    const was = grip.current;
    grip.current = {
      target,
      grip: gripOf(points, target === null ? CAPTION_HOME : placeOf(target)),
      moved: was?.moved ?? false,
      first: was?.first ?? target,
      many: (was?.many ?? false) || points.length > 1,
    };
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (stretching.current) return;
    const edge = (event.target as Element).closest?.('[data-edge]');
    if (edge && fingers.current.size === 0) {
      const words = edge.closest('[data-caption]') as HTMLElement | null;
      const across = frame.current?.offsetWidth ?? 0;
      event.currentTarget.setPointerCapture?.(event.pointerId);
      stretching.current = {
        id: event.pointerId,
        edge: edge.getAttribute('data-edge') === 'left' ? 'left' : 'right',
        start: { x: event.clientX, y: event.clientY },
        from: captionAt,
        // As wide as it is drawn now, when no width has been chosen yet.
        width: captionAt.width ?? (words && across ? words.offsetWidth / across : 0.5),
      };
      tapped.current = null;
      active.current = 'caption';
      setHeld('caption');
      return;
    }
    if (fingers.current.size >= 2) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    fingers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const under = targetOf(event.target);
    tapped.current = null;

    if (fingers.current.size === 1) {
      grip.current = null;
      retake(under);
    } else {
      // The second finger: whatever the first is holding, else whatever this
      // one landed on, else the last thing touched.
      retake(grip.current?.target ?? under ?? fallback());
    }
    const target = grip.current?.target ?? null;
    if (target !== null) {
      active.current = target;
      setHeld(target);
    }
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    const stretch = stretching.current;
    if (stretch) {
      const frameBox = frame.current?.getBoundingClientRect();
      if (stretch.id !== event.pointerId || !frameBox) return;
      setCaptionAt(
        stretchAt(
          stretch.from,
          stretch.width,
          stretch.edge,
          { x: event.clientX - stretch.start.x, y: event.clientY - stretch.start.y },
          frameBox,
        ),
      );
      return;
    }
    const now = grip.current;
    const box = frame.current?.getBoundingClientRect();
    if (!now || !box || !fingers.current.has(event.pointerId)) return;
    fingers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = [...fingers.current.values()];
    if (hasMoved(now.grip, points)) now.moved = true;
    if (now.target !== null && now.moved) {
      // Within reach of the picture, and on the screen: nothing can be put
      // where it cannot be seen to be picked up again.
      const reach = latest.current.reach ?? PHOTO_REACH;
      const seen = event.currentTarget.getBoundingClientRect();
      place(
        now.target,
        placeAt(now.grip, points, box, {
          x0: Math.max(reach.x0, (seen.left - box.left) / box.width),
          x1: Math.min(reach.x1, (seen.right - box.left) / box.width),
          y0: Math.max(reach.y0, (seen.top - box.top) / box.height),
          y1: Math.min(reach.y1, (seen.bottom - box.top) / box.height),
        }),
      );
    }
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLElement>) => {
    if (stretching.current?.id === event.pointerId) {
      stretching.current = null;
      tapped.current = null;
      setHeld(null);
      return;
    }
    if (!fingers.current.delete(event.pointerId)) return;
    const now = grip.current;
    if (fingers.current.size === 0) {
      // A tap is acted on in the click that follows, not here: open the words
      // now and that click lands on the typing layer that just opened, and
      // reads as a tap off the words that closes it again.
      tapped.current = now && !now.moved ? { target: now.first } : null;
      // One finger across the picture, holding nothing: the look changes.
      if (now && now.moved && now.target === null && !now.many) {
        const step = swipeOf(now.grip, { x: event.clientX, y: event.clientY });
        if (step !== 0) latest.current.onSwipe?.(step);
      }
      grip.current = null;
      setHeld(null);
      return;
    }
    // A finger lifted mid-pinch: carry on from where it is with what is left.
    if (now) retake(now.target);
  };

  const onPointerCancel = (event: ReactPointerEvent<HTMLElement>) => {
    if (stretching.current?.id === event.pointerId) {
      stretching.current = null;
      setHeld(null);
      return;
    }
    fingers.current.delete(event.pointerId);
    if (fingers.current.size === 0) {
      grip.current = null;
      tapped.current = null;
      setHeld(null);
    }
  };

  /**
   * A tap: on the words, retype them; on an emoji, choose it (or let it go);
   * on the picture or round it, write, as Snapchat does -- unless an emoji is
   * chosen, when the tap only lets go of it.
   */
  const onClick = () => {
    const tap = tapped.current;
    tapped.current = null;
    if (!tap) return;
    const target = tap.target;
    if (target === 'caption') {
      setWriting(true);
    } else if (typeof target === 'number') {
      setChosen(chosen === target ? null : target);
    } else if (chosen !== null) {
      setChosen(null);
    } else {
      setWriting(true);
    }
  };

  const layer = (
    <span ref={frame} className="snap-decor is-editing">
      {stickers.map((sticker, index) => {
        const name = 'art' in sticker ? sticker.art.replace(/-/g, ' ') : sticker.emoji;
        return (
          <span
            key={index}
            data-sticker={index}
            className={
              'snap-sticker is-editing' +
              ('art' in sticker ? ' snap-sticker--art' : '') +
              (chosen === index ? ' is-chosen' : '') +
              (held === index ? ' is-held' : '')
            }
            style={styleFor(sticker.at)}
            aria-label={chosen === index ? `${name}, chosen` : name}
          >
            {'art' in sticker ? <img src={artUrl(sticker.art)} alt="" draggable={false} /> : sticker.emoji}
          </span>
        );
      })}
      {shown && !writing && (
        <span
          data-caption=""
          className={`caption ${lookClasses(captionStyle)} is-editing${held === 'caption' ? ' is-held' : ''}`}
          style={styleFor(captionAt)}
        >
          {caption}
          {/* Grabbed to make the box wider or narrower from that side. */}
          <span data-edge="left" className="caption-edge caption-edge--left" aria-hidden="true" />
          <span data-edge="right" className="caption-edge caption-edge--right" aria-hidden="true" />
        </span>
      )}
    </span>
  );

  const tools = (
    <>
      <button
        type="button"
        className="snap-editor-key"
        aria-label={shown ? 'Change the text' : 'Add text'}
        aria-pressed={writing}
        onClick={() => setWriting(true)}
      >
        Aa
      </button>
      <button
        type="button"
        className="snap-editor-key"
        aria-label="Add an emoji"
        disabled={full}
        onClick={() => setPicking(true)}
      >
        <IconSmile size={20} />
      </button>
      {chosen !== null && (
        <button type="button" className="snap-editor-key" aria-label="Remove emoji" onClick={removeChosen}>
          <IconTrash size={20} />
        </button>
      )}
    </>
  );

  const overlays = (
    <>
      {/*
        * Typing: the words high on the screen over a dimmed picture, clear of
        * the keyboard, as big as they will be. Done, Enter or a tap anywhere
        * else puts the keyboard away and leaves the words where they were.
        */}
      {writing && (
        <form
          className="snap-editor-typing"
          onSubmit={(event) => {
            event.preventDefault();
            setWriting(false);
          }}
          onClick={(event) => {
            if (event.target === event.currentTarget) setWriting(false);
          }}
        >
          <div className="snap-editor-typing-stack">
            <input
              className={`snap-editor-input ${lookClasses(captionStyle)}`}
              value={caption}
              maxLength={SNAP_CAPTION_MAX}
              placeholder="Say something"
              aria-label="Text on the snap"
              enterKeyHint="done"
              autoFocus
              onChange={(event) => setCaption(event.target.value)}
              onBlur={() => setWriting(false)}
            />
            {/* The words' face and colour, changed as they are typed. */}
            <div className="caption-fonts" role="group" aria-label="Font">
              {CAPTION_FONTS.map((font) => (
                <button
                  key={font}
                  type="button"
                  className="caption-font"
                  aria-label={FONT_NAMES[font]}
                  aria-pressed={captionStyle.font === font}
                  onPointerDown={keepTyping}
                  onMouseDown={keepTyping}
                  onClick={() => setCaptionStyle((now) => ({ ...now, font }))}
                >
                  <span className={`caption--${font}`}>Aa</span>
                </button>
              ))}
            </div>
            <div className="caption-colors" role="group" aria-label="Colour">
              {CAPTION_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`caption-swatch caption--${color}`}
                  aria-label={COLOR_NAMES[color]}
                  aria-pressed={captionStyle.color === color}
                  onPointerDown={keepTyping}
                  onMouseDown={keepTyping}
                  onClick={() => setCaptionStyle((now) => ({ ...now, color }))}
                />
              ))}
            </div>
          </div>
        </form>
      )}

      {picking && <EmojiPicker over onPick={addSticker} onClose={() => setPicking(false)} />}
    </>
  );

  /** Several stickers at once, as many as there is room for: face stickers, laid on every face. */
  const addStickers = (more: SnapSticker[]) => {
    setStickers((now) => {
      const next = [...now, ...more].slice(0, SNAP_STICKERS_MAX);
      if (next.length > now.length) active.current = next.length - 1;
      return next;
    });
    setChosen(null);
  };

  return {
    decor: decorFrom(caption, captionAt, stickers, captionStyle),
    addStickers,
    /** How many more stickers there is room for. */
    room: SNAP_STICKERS_MAX - stickers.length,
    /** Nothing on it and nothing being typed: the time to say a tap will write. */
    blank: !shown && stickers.length === 0 && !writing,
    surface: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClick },
    layer,
    tools,
    overlays,
  };
}
